import { gridViewManager } from '@/utils/grid'
import { useMetadata } from '@api/useMetadata'
import { DateRangeFilter } from '@components/_SiteWide/Grid/ActionButtons/DateRangeFilter'
import { DownloadButton } from '@components/_SiteWide/Grid/ActionButtons/DownloadButton'
import type { HierarchyData } from '@components/_SiteWide/Grid/CellEditors/HierarchySelectEditor'
import type {
  FormConfiguration,
  MarketHierarchy,
  SupplyZoneHierarchy,
} from '@components/DriverManagement/DriverForms/api/schema'
import { useFormAPI } from '@components/DriverManagement/DriverForms/api/useFormsAPI'
import { getColumnDefs } from '@components/DriverManagement/DriverForms/components/FormGrid/columnDefs'
import { GraviGrid, Horizontal } from '@gravitate-js/excalibrr'
import { useUserSettings } from '@hooks/settings'
import React, { useMemo, useRef } from 'react'

const STORAGE_KEY = 'forms-grid'

function transformSupplyZoneHierarchy(supplyZoneHierarchy: SupplyZoneHierarchy[]): HierarchyData[] {
  return supplyZoneHierarchy.map((zone) => ({
    id: zone.id,
    name: zone.name,
    level: 'Supply Zone',
    children: zone.terminals.map((terminal) => ({
      id: terminal.id,
      name: terminal.name,
    })),
  }))
}

function transformMarketHierarchy(marketHierarchy: MarketHierarchy[]): HierarchyData[] {
  return marketHierarchy.map((market) => ({
    id: market.id,
    name: market.name,
    level: 'Market',
    children: market?.sectors?.map((sector) => ({
      id: sector.id,
      name: sector.name,
      level: 'Sector',
      children: sector?.sites?.map((site) => ({
        id: site.id,
        name: site.name,
      })),
    })),
  }))
}

export function FormGrid() {
  const gridAPIRef = useRef(null)
  const { updateSetting } = useUserSettings()

  const { useFormsOverview, useFormsUpsert, useFormsMetadata } = useFormAPI()

  const { data: metadata, isPending: isMetaLoading } = useFormsMetadata()

  const supplyZoneHierarchy = useMemo(() => {
    if (!metadata) return []
    return transformSupplyZoneHierarchy(metadata.supply_zones)
  }, [metadata])

  const marketHierarchy = useMemo(() => {
    if (!metadata) return []
    return transformMarketHierarchy(metadata.markets)
  }, [metadata])

  const handleStatusToggle = async (form: FormConfiguration) => {
    const result = await useFormsUpsert.mutateAsync({ ...form, enabled: !form.enabled })
    gridAPIRef?.current?.applyTransaction({ update: [result] })
  }

  const handleUpdate = async (values: FormConfiguration) => {
    const result = await useFormsUpsert.mutateAsync(values)
    gridAPIRef?.current?.applyTransaction({ update: [result] })
  }

  const columnDefs = getColumnDefs(marketHierarchy, supplyZoneHierarchy, handleStatusToggle)

  const agPropOverrides = useMemo(() => {
    return {
      getRowId: (row) => row.data?._id,
      groupDisplayType: 'multipleColumns',
      suppressAggFuncInHeader: true,
    }
  }, [])

  const controlBarProps = useMemo(() => {
    return {
      title: 'Form Configuration',
    }
  }, [])

  const {
    data,
    isPending: isLoadingOverview,
    isFetching: isFetchingOverview,
  } = useFormsOverview() as {
    data: FormConfiguration[] | undefined
    isPending: boolean
    isFetching: boolean
  }

  const rowData: FormConfiguration[] = useMemo(() => {
    return structuredClone(data || [])
  }, [data])

  return (
    <GraviGrid
      columnDefs={columnDefs}
      agPropOverrides={agPropOverrides}
      controlBarProps={controlBarProps}
      primaryKey='_id'
      storageKey={STORAGE_KEY}
      onGridConfigChanged={(config) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
      loading={isLoadingOverview || isFetchingOverview || isMetaLoading}
      rowData={rowData}
      updateEP={handleUpdate}
      externalRef={gridAPIRef}
      {...gridViewManager(STORAGE_KEY, updateSetting)}
    />
  )
}
